<template>
    <v-dialog v-model="show" max-width="680" persistent>
        <v-card dark style="background:#161b22">
            <v-card-title class="text-subtitle-1 py-2">
                <v-icon left :color="running ? 'orange' : (success === true ? 'green' : success === false ? 'red' : 'grey')">
                    mdi-console
                </v-icon>
                {{ title }}
                <v-spacer />
                <v-btn icon small :disabled="running" @click="show = false">
                    <v-icon small>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            <v-progress-linear
                :indeterminate="running"
                :value="running ? undefined : 100"
                :color="running ? 'orange' : (success ? 'green' : 'red')"
                height="2"
            />
            <v-card-text class="pa-2">
                <div
                    ref="console"
                    style="font-family:'Courier New',monospace;font-size:0.78rem;background:#0d1117;
                           color:#e6edf3;height:280px;overflow-y:auto;padding:12px;border-radius:4px;line-height:1.6"
                >
                    <div v-for="(line, i) in lines" :key="i" :style="lineStyle(line)">{{ line }}</div>
                    <span v-if="running" style="color:#3fb950">&#9612;</span>
                </div>
            </v-card-text>
            <v-card-actions class="pt-0">
                <v-chip x-small :color="running ? 'orange' : (success ? 'green' : 'red')" text-color="white">
                    {{ running ? 'Running…' : (success ? 'Done' : 'Error') }}
                </v-chip>
                <v-spacer />
                <v-btn text small :disabled="running" @click="show = false">Close</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'CronConsoleModal',

    data: () => ({
        show:    false,
        running: false,
        success: null,
        title:   '',
        lines:   [],
        _abort:  null,   // AbortController for the active fetch
    }),

    methods: {
        /**
         * Open the console and stream a cron job.
         * @param {string} title  — label shown in the dialog title bar
         * @param {object} params — query params: { job, from, to, staff_ismid?, reprocess? }
         */
        async run(title, params) {
            this.title   = title
            this.lines   = [`$ ${title}`, `params: ${JSON.stringify(params)}`]
            this.running = true
            this.success = null
            this.show    = true

            // Cancel any previous in-flight request
            if (this._abort) {
                this._abort.abort()
                this._abort = null
            }

            const qs      = new URLSearchParams(
                Object.entries(params)
                    .filter(([, v]) => v != null)
                    .map(([k, v]) => [k, typeof v === 'boolean' ? (v ? '1' : '0') : v])
            ).toString()
            const baseURL = (this.$axios?.defaults?.baseURL || '').replace(/\/$/, '')
            const url     = `${baseURL}/api/cron-jobs/stream?${qs}`

            const controller = new AbortController()
            this._abort = controller

            try {
                const response = await fetch(url, {
                    credentials: 'include',
                    headers: { 'Accept': 'application/json, text/event-stream', 'X-Requested-With': 'XMLHttpRequest' },
                    signal: controller.signal,
                })

                if (!response.ok) {
                    const body = await response.text().catch(() => '')
                    // Try to extract Laravel validation / error message
                    let msg = `HTTP ${response.status}`
                    try {
                        const json = JSON.parse(body)
                        msg += ': ' + (json.message || JSON.stringify(json))
                    } catch { if (body) msg += ': ' + body.slice(0, 200) }
                    this.lines.push('ERROR: ' + msg)
                    this.success = false
                    this.running = false
                    return
                }

                const reader  = response.body.getReader()
                const decoder = new TextDecoder()
                let   buffer  = ''

                // eslint-disable-next-line no-constant-condition
                while (true) {
                    const { done, value } = await reader.read()
                    if (done) break

                    buffer += decoder.decode(value, { stream: true })

                    // SSE events are separated by double newlines
                    const parts = buffer.split('\n\n')
                    buffer = parts.pop()   // keep the incomplete trailing chunk

                    for (const part of parts) {
                        const match = part.match(/^data:\s*(.+)$/m)
                        if (!match) continue
                        const msg = JSON.parse(match[1])
                        if (msg.done) {
                            this.success = msg.exit_code === 0
                            this.running = false
                            this.$emit('done')
                        } else if (msg.line) {
                            this.lines.push(msg.line)
                        }
                        this.$nextTick(() => {
                            const el = this.$refs.console
                            if (el) el.scrollTop = el.scrollHeight
                        })
                    }
                }
            } catch (e) {
                if (e.name !== 'AbortError') {
                    this.lines.push('ERROR: ' + (e.message || 'Connection failed'))
                    this.success = false
                    this.running = false
                }
            } finally {
                this._abort = null
            }
        },

        lineStyle(line) {
            if (/^(ERROR|error)/i.test(line))                 return { color: '#f85149' }
            if (/^(Done|Processed|Cancelled|Synced)/i.test(line)) return { color: '#3fb950' }
            if (line.startsWith('$'))                          return { color: '#79c0ff' }
            return {}
        },
    },

    beforeDestroy() {
        if (this._abort) {
            this._abort.abort()
            this._abort = null
        }
    },
}
</script>
